<template>
  <div class="unified-documents-table">
    <!-- Loading state -->
    <LoadingIndicatorComponent v-if="isLoading" />

    <!-- Table -->
    <div v-else>
      <DataTable
        :value="documents"
        dataKey="compositeKey"
        filterDisplay="row"
        v-model:filters="filters"
        :globalFilterFields="['name', 'company_name', 'category', 'status']"
        class="border rounded"
        :sortField="'created_at'"
        :sortOrder="-1"
      >
        <template #empty>
          <div class="text-center py-4 text-muted">
            {{ t("documents_table.no_documents") }}
          </div>
        </template>

        <!-- ID Column (admin only) -->
        <Column
          v-if="isAdmin"
          field="id"
          :header="t('documents_table.columns.id')"
          sortable
          :headerStyle="{ width: '5rem' }"
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <span class="alert alert-subtle-warning p-1 mb-0"
              >#{{ data.id }}</span
            >
          </template>
        </Column>

        <!-- Name -->
        <Column
          field="name"
          :header="t('documents_table.columns.name')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          class="wrap-text"
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter w-100"
              :placeholder="t('documents_table.filters.search_name')"
            />
          </template>
          <template #body="{ data }">
            <div class="d-flex align-items-center">
              <FontAwesomeIcon
                :icon="getFileIcon(data)"
                class="me-2 text-muted"
              />
              <span class="text-truncate">{{ data.name }}</span>
            </div>
          </template>
        </Column>

        <!-- Type -->
        <Column
          field="document_type"
          :header="t('documents_table.columns.type')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: '10rem' }"
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            {{ formatDocumentType(data.document_type) }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="uniqueTypes"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Category -->
        <Column
          field="category"
          :header="t('documents_table.columns.category')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: '12rem' }"
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            {{ formatCategory(data.category) }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="uniqueCategories"
              searchable
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Company (consolidated view only) -->
        <Column
          v-if="showCompanyColumn"
          field="company_name"
          :header="t('documents_table.columns.company')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: '10rem' }"
          class="wrap-text"
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter w-100"
              :placeholder="t('documents_table.filters.search_company')"
            />
          </template>
        </Column>

        <!-- Status -->
        <Column
          field="status"
          :header="t('documents_table.columns.status')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: '10rem' }"
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <span
              class="badge rounded-pill"
              :class="getStatusBadgeClass(data.status)"
            >
              {{ formatStatus(data.status) }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="uniqueStatuses"
              @filter="filterCallback()"
            >
              <template #option="{ option }">
                <span
                  class="badge rounded-pill"
                  :class="getStatusBadgeClass(String(option.value))"
                >
                  {{ option.label }}
                </span>
              </template>
            </ColumnMultiSelectFilter>
          </template>
        </Column>

        <!-- Created At -->
        <Column
          field="created_at"
          :header="t('documents_table.columns.created')"
          sortable
          :headerStyle="{ width: '9rem' }"
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            {{ formatDate(data.created_at) }}
          </template>
        </Column>

        <!-- Actions -->
        <Column
          :header="t('documents_table.columns.actions')"
          :headerStyle="{ width: '18rem' }"
        >
          <template #body="{ data }">
            <div class="d-flex gap-1 flex-wrap">
              <!-- Download -->
              <button
                class="btn btn-sm btn-outline-primary"
                @click="handleDownload(data)"
                :title="t('common.download')"
              >
                <FontAwesomeIcon icon="download" class="me-1" />
                {{ t("common.download") }}
              </button>

              <!-- Sign (dokobit documents only) -->
              <a
                v-if="data.can_sign && data.sign_url"
                :href="data.sign_url"
                target="_blank"
                class="btn btn-sm btn-primary"
                :title="t('document.sign_document')"
                @click.stop
              >
                <FontAwesomeIcon icon="signature" class="me-1" />
                {{ t("documents_table.actions.sign") }}
              </a>

              <!-- View Details (only when document has details to show) -->
              <button
                v-if="hasDocumentDetails(data)"
                class="btn btn-sm btn-outline-secondary"
                @click="openDetailsModal(data)"
                :title="t('documents_table.actions.view_details')"
              >
                <FontAwesomeIcon icon="eye" class="me-1" />
                {{ t("documents_table.actions.view_details") }}
              </button>

              <!-- Archive (custom uploads only) -->
              <button
                v-if="data.can_archive"
                class="btn btn-sm btn-outline-danger"
                @click="handleArchive(data)"
                :title="t('documents_table.actions.archive')"
              >
                <FontAwesomeIcon icon="trash" class="me-1" />
                {{ t("documents_table.actions.archive") }}
              </button>

              <!-- Admin Section (admin only, dokobit documents) -->
              <span
                v-if="isAdmin && data.dokobit_token"
                class="alert alert-subtle-warning p-1 mb-0 d-inline-flex flex-column align-items-start"
              >
                <div class="d-flex gap-1 flex-wrap">
                  <a
                    v-if="data.generator_id"
                    :href="`/signup/producer?step=2&generator_id=${data.generator_id}&company_id=${data.company_id}`"
                    class="btn btn-sm btn-outline-warning"
                    :title="t('common.edit')"
                  >
                    <FontAwesomeIcon icon="pen" class="me-1" />
                    {{ t("common.edit") }}
                  </a>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="handleAdminDelete(data)"
                    :title="t('document.admin_delete_document')"
                  >
                    <FontAwesomeIcon icon="trash" class="me-1" />
                    {{ t("document.admin_delete_document") }}
                  </button>
                </div>
                <small v-if="data.generator_id" class="text-muted mt-1">
                  Generator ID: {{ data.generator_id }}
                </small>
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Document Details Modal -->
    <ModalComponent
      ref="detailsModalRef"
      :submit-button-text="t('common.close')"
      :hide-submit-button="false"
      @close="selectedDocument = null"
    >
      <template #title>
        {{ t("documents_table.details_modal.title") }}
      </template>
      <template #body>
        <div v-if="selectedDocument">
          <!-- Document info summary -->
          <div class="mb-4">
            <table class="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td class="text-muted fw-semibold details-label-cell">
                    {{ t("documents_table.columns.name") }}
                  </td>
                  <td>
                    <FontAwesomeIcon
                      :icon="getFileIcon(selectedDocument)"
                      class="me-2 text-muted"
                    />
                    {{ selectedDocument.name }}
                  </td>
                </tr>
                <tr>
                  <td class="text-muted fw-semibold">
                    {{ t("documents_table.columns.type") }}
                  </td>
                  <td>
                    {{ formatDocumentType(selectedDocument.document_type) }}
                  </td>
                </tr>
                <tr>
                  <td class="text-muted fw-semibold">
                    {{ t("documents_table.columns.category") }}
                  </td>
                  <td>{{ formatCategory(selectedDocument.category) }}</td>
                </tr>
                <tr>
                  <td class="text-muted fw-semibold">
                    {{ t("documents_table.columns.status") }}
                  </td>
                  <td>
                    <span
                      class="badge rounded-pill"
                      :class="getStatusBadgeClass(selectedDocument.status)"
                    >
                      {{ formatStatus(selectedDocument.status) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="selectedDocument.company_name">
                  <td class="text-muted fw-semibold">
                    {{ t("documents_table.columns.company") }}
                  </td>
                  <td>{{ selectedDocument.company_name }}</td>
                </tr>
                <tr>
                  <td class="text-muted fw-semibold">
                    {{ t("documents_table.columns.created") }}
                  </td>
                  <td>{{ formatDate(selectedDocument.created_at) }}</td>
                </tr>
                <tr v-if="selectedDocument.upload_source">
                  <td class="text-muted fw-semibold">
                    {{ t("documents_table.details_modal.source") }}
                  </td>
                  <td>{{ selectedDocument.upload_source }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Dokobit contract details -->
          <div
            v-if="
              selectedDocument.document_type === 'dokobit_document' &&
              selectedDocument.contract_keys.length > 0
            "
          >
            <h6 class="fw-semibold mb-3 border-bottom pb-2">
              {{ t("documents_table.details_modal.contract_details") }}
            </h6>
            <div
              v-for="key in selectedDocument.contract_keys"
              :key="key"
              class="mb-3"
            >
              <div class="fw-semibold text-dark">
                {{ getContractTitle(key, selectedDocument) }}
              </div>
              <div class="text-muted">
                {{ getContractDescription(key, selectedDocument) }}
              </div>
            </div>
          </div>

          <!-- Linked cancellations -->
          <div
            v-if="
              selectedDocument.document_type === 'custom_upload' &&
              selectedDocument.cancellation_ids.length > 0
            "
          >
            <h6 class="fw-semibold mb-3 border-bottom pb-2">
              {{ t("documents_table.linked_cancellations") }}
            </h6>
            <div class="d-flex gap-2 flex-wrap">
              <span
                v-for="cId in selectedDocument.cancellation_ids"
                :key="cId"
                class="badge bg-info-subtle text-info-emphasis"
              >
                {{ t("documents_table.cancellation_id", { id: cId }) }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </ModalComponent>
  </div>
</template>

<script setup lang="ts">
import ColumnMultiSelectFilter from "@/components/common/ColumnMultiSelectFilter.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import { useFileDownload } from "@/composables/useFileDownload";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Company } from "@/types";
import { predefinedDocuments } from "@/types/PredefinedDocuments";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { UnifiedDocument } from "./documentTypes";
import { archiveUpload, fetchAllDocuments } from "./documentsApi";
import { getSortIcon } from "@/utils/sortIcons";

const props = defineProps<{
  companies: Company[];
  isAdmin: boolean;
  showCompanyColumn: boolean;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { downloadCompanyDocument, downloadDocumentByToken } = useFileDownload();

const documents = ref<(UnifiedDocument & { compositeKey: string })[]>([]);
const isLoading = ref(false);

// Details modal
const detailsModalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("detailsModalRef");
const selectedDocument = ref<UnifiedDocument | null>(null);

// Filters
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  document_type: { value: [], matchMode: FilterMatchMode.IN },
  category: { value: [], matchMode: FilterMatchMode.IN },
  company_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: [], matchMode: FilterMatchMode.IN },
});

// Data fetching
const fetchDocuments = async () => {
  const companyIds = props.companies
    .map((c) => c.id)
    .filter((id): id is number => typeof id === "number");

  if (companyIds.length === 0) return;

  isLoading.value = true;
  try {
    const responses = await Promise.all(
      companyIds.map((companyId) => fetchAllDocuments(companyId))
    );

    const allDocs = responses.flatMap((response) => response.documents);

    const getTimeSafe = (dateString: string | null): number =>
      dateString ? Date.parse(dateString) || 0 : 0;

    // Add composite key for DataTable since IDs can overlap between types
    documents.value = allDocs
      .sort((a, b) => getTimeSafe(b.created_at) - getTimeSafe(a.created_at))
      .map((doc) => ({
        ...doc,
        compositeKey: `${doc.document_type}_${doc.id}`,
      }));
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

// Fetch on mount and re-fetch when companies prop changes (may arrive async)
watch(
  () => props.companies,
  (newCompanies) => {
    if (newCompanies.length > 0) {
      fetchDocuments();
    }
  },
  { immediate: true }
);

// Check if a specific document has details worth showing
const hasDocumentDetails = (doc: UnifiedDocument): boolean => {
  if (
    doc.document_type === "dokobit_document" &&
    doc.contract_keys.length > 0
  ) {
    return true;
  }
  if (
    doc.document_type === "custom_upload" &&
    doc.cancellation_ids.length > 0
  ) {
    return true;
  }
  return false;
};

const openDetailsModal = (doc: UnifiedDocument) => {
  selectedDocument.value = doc;
  detailsModalRef.value?.toggleModal();
};

// Filter options
const uniqueTypes = computed(() => {
  const types = new Set(documents.value.map((d) => d.document_type));
  return [...types].map((val) => ({
    label: formatDocumentType(val),
    value: val,
  }));
});

const uniqueCategories = computed(() => {
  const categories = new Set(documents.value.map((d) => d.category));
  return [...categories].sort().map((val) => ({
    label: formatCategory(val),
    value: val,
  }));
});

const uniqueStatuses = computed(() => {
  const statuses = new Set(documents.value.map((d) => d.status));
  return [...statuses].map((val) => ({
    label: formatStatus(val),
    value: val,
  }));
});

// Formatters
const formatDocumentType = (type: string): string => {
  return t(`documents_table.type.${type}`, type);
};

const formatCategory = (category: string): string => {
  // Try predefined documents first (for dokobit contract keys)
  const predefined = predefinedDocuments[category];
  if (predefined) {
    return t(predefined.contract_title, category);
  }
  // Then try translation key
  return t(`documents_table.category.${category}`, category);
};

const formatStatus = (status: string): string => {
  return t(`documents_table.status.${status}`, status);
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  return formatDateTimeIntoDate(dateString);
};

const getStatusBadgeClass = (status: string): string => {
  if (status === "completed") return "bg-success-subtle text-success-emphasis";
  if (status === "awaiting_signature")
    return "bg-warning-subtle text-warning-emphasis";
  if (status === "uploaded") return "bg-info-subtle text-info-emphasis";
  if (status === "canceled")
    return "bg-secondary-subtle text-secondary-emphasis";
  return "bg-secondary-subtle text-secondary-emphasis";
};

const getFileIcon = (doc: UnifiedDocument): string => {
  if (doc.document_type === "dokobit_document") return "file-signature";
  const name = (doc.name ?? "").toLowerCase();
  if (name.endsWith(".pdf")) return "file-pdf";
  if (name.endsWith(".doc") || name.endsWith(".docx")) return "file-word";
  if (name.endsWith(".xls") || name.endsWith(".xlsx")) return "file-excel";
  if (name.endsWith(".zip")) return "file-zipper";
  return "file";
};

// Contract details helpers
const getContractTitle = (key: string, doc: UnifiedDocument): string => {
  if (doc.file_metadata?.[key]?.title) {
    return doc.file_metadata[key].title;
  }
  const predefined = predefinedDocuments[key];
  if (predefined) {
    return t(predefined.contract_title);
  }
  return key;
};

const getContractDescription = (key: string, doc: UnifiedDocument): string => {
  if (doc.file_metadata?.[key]?.description) {
    return doc.file_metadata[key].description;
  }
  const predefined = predefinedDocuments[key];
  if (predefined) {
    return t(predefined.contract_description);
  }
  return "";
};

// Actions
const handleDownload = async (doc: UnifiedDocument) => {
  try {
    if (doc.document_type === "custom_upload") {
      await downloadCompanyDocument(doc.company_id, doc.id);
    } else if (doc.dokobit_token) {
      await downloadDocumentByToken(doc.dokobit_token);
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

const handleArchive = async (doc: UnifiedDocument) => {
  const confirmed = window.confirm(t("documents_table.confirm_archive"));
  if (!confirmed) return;

  try {
    await archiveUpload(doc.company_id, doc.id);
    globalSuccessHandler(
      t("documents_table.archive_success_title"),
      t("documents_table.archive_success_message")
    );
    await fetchDocuments();
  } catch (error) {
    globalErrorHandler(error);
  }
};

const handleAdminDelete = async (doc: UnifiedDocument) => {
  if (!doc.dokobit_token) return;

  const confirmed = window.confirm(t("document.confirm_admin_delete"));
  if (!confirmed) return;

  try {
    await goSolidApi.delete(`/api/admin/document/${doc.dokobit_token}`);
    await fetchDocuments();
  } catch (error) {
    globalErrorHandler(error);
  }
};
</script>

<style scoped lang="scss">
.wrap-text {
  word-break: break-word;
}

.details-label-cell {
  width: 140px;
}
</style>
